// Static interface copy in every supported app language. User and model content is not translated here.
import type { NamespaceCatalog } from '@/i18n/ns';

const en = {
  filterAll: 'All',
  filterEntries: 'Entries',
  filterApprovals: 'Approvals',
  filterChanges: 'Changes',
  filterPublishes: 'Publishes',
  filtersLabel: 'Filter the history',
  note: 'People who use the app see only their own entries here.',
  today: 'Today',
  yesterday: 'Yesterday, {day}',
  empty: 'Nothing here yet.',
  emptyFiltered: 'Nothing matches this filter.',
  approvedTitle: 'Approved: {what}',
  declinedTitle: 'Declined: {what}',
  approvalDetail: 'Proposed by {proposer} · decided by {decider}',
  approvalWritten: '{done} of {total} written',
  viewRecord: 'View record',
  changeTitle: '{title} · draft v{version}',
  changeFor: 'by the builder, for {who}',
  publishTitle: 'Published v{version} to {audience}',
  publishDetail: '{people} people',
  publishRequested: 'By {who}, at {requester}’s request',
  unpublishTitle: 'Unpublished v{version}',
  restoreTitle: 'Restored {what}',
  restoreTarget: 'To {target}',
  refreshOk: 'Refreshed',
  refreshFailed: 'Refresh failed',
  sourceViewed: 'Source viewed: {path}',
  skippedTitle: '{run} skipped {part}',
  skippedBody:
    'It reads each person’s own {system}, and this run has no person to read as. Choose who it runs as, or switch {part} to your connection.',
  blockedApproval: '{title} · waiting for approval',
  blockedBudget: '{title} · paused at the budget',
  blockedProvider: '{title} · waiting for the provider',
  chooseRunAs: 'Choose who it runs as',
  changeInChat: 'Change in chat',
  viaOrigin: 'via {label}',
  byBuilder: 'by the builder',
};

export type AppHistoryCopyKey = keyof typeof en;

export const appHistoryCopy: NamespaceCatalog<keyof typeof en> = {
  en: {
    filterAll: 'All',
    filterEntries: 'Entries',
    filterApprovals: 'Approvals',
    filterChanges: 'Changes',
    filterPublishes: 'Publishes',
    filtersLabel: 'Filter the history',
    note: 'People who use the app see only their own entries here.',
    today: 'Today',
    yesterday: 'Yesterday, {day}',
    empty: 'Nothing here yet.',
    emptyFiltered: 'Nothing matches this filter.',
    approvedTitle: 'Approved: {what}',
    declinedTitle: 'Declined: {what}',
    approvalDetail: 'Proposed by {proposer} · decided by {decider}',
    approvalWritten: '{done} of {total} written',
    viewRecord: 'View record',
    changeTitle: '{title} · draft v{version}',
    changeFor: 'by the builder, for {who}',
    publishTitle: 'Published v{version} to {audience}',
    publishDetail: '{people} people',
    publishRequested: 'By {who}, at {requester}’s request',
    unpublishTitle: 'Unpublished v{version}',
    restoreTitle: 'Restored {what}',
    restoreTarget: 'To {target}',
    refreshOk: 'Refreshed',
    refreshFailed: 'Refresh failed',
    sourceViewed: 'Source viewed: {path}',
    skippedTitle: '{run} skipped {part}',
    skippedBody:
      'It reads each person’s own {system}, and this run has no person to read as. Choose who it runs as, or switch {part} to your connection.',
    blockedApproval: '{title} · waiting for approval',
    blockedBudget: '{title} · paused at the budget',
    blockedProvider: '{title} · waiting for the provider',
    chooseRunAs: 'Choose who it runs as',
    changeInChat: 'Change in chat',
    viaOrigin: 'via {label}',
    byBuilder: 'by the builder',
  },
  bg: {
    filterAll: 'Всички',
    filterEntries: 'Записи',
    filterApprovals: 'Одобрения',
    filterChanges: 'Промени',
    filterPublishes: 'Публикации',
    filtersLabel: 'Филтриране на историята',
    note: 'Хората, които използват приложението, виждат тук само собствените си записи.',
    today: 'Днес',
    yesterday: 'Вчера, {day}',
    empty: 'Все още няма нищо тук.',
    emptyFiltered: 'Нищо не съответства на този филтър.',
    approvedTitle: 'Одобрено: {what}',
    declinedTitle: 'Отхвърлено: {what}',
    approvalDetail: 'Предложено от {proposer} · решено от {decider}',
    approvalWritten: 'Записани са {done} от {total}',
    viewRecord: 'Преглед на записа',
    changeTitle: '{title} · чернова v{version}',
    changeFor: 'от създателя, за {who}',
    publishTitle: 'Публикувано v{version} за {audience}',
    publishDetail: '{people} души',
    publishRequested: 'От {who}, по искане на {requester}',
    unpublishTitle: 'Публикувано е извън приложението v{version}',
    restoreTitle: 'Възстановено: {what}',
    restoreTarget: 'Към {target}',
    refreshOk: 'Обновено',
    refreshFailed: 'Обновяването не бе успешно',
    sourceViewed: 'Източникът е прегледан: {path}',
    skippedTitle: '{run} пропусна {part}',
    skippedBody:
      'То чете собствения {system} на всеки човек, а за това изпълнение няма човек, от чието име да чете. Изберете от чие име да се изпълни или превключете {part} към вашата връзка.',
    blockedApproval: '{title} · изчаква одобрение',
    blockedBudget: '{title} · поставено на пауза заради бюджета',
    blockedProvider: '{title} · изчаква доставчика',
    chooseRunAs: 'Изберете от чие име да се изпълни',
    changeInChat: 'Промяна в чата',
    viaOrigin: 'чрез {label}',
    byBuilder: 'от създателя',
  },
  cs: {
    filterAll: 'Vše',
    filterEntries: 'Záznamy',
    filterApprovals: 'Schválení',
    filterChanges: 'Změny',
    filterPublishes: 'Publikování',
    filtersLabel: 'Filtrovat historii',
    note: 'Lidé, kteří aplikaci používají, zde uvidí pouze své vlastní záznamy.',
    today: 'Dnes',
    yesterday: 'Včera, {day}',
    empty: 'Zatím zde nic není.',
    emptyFiltered: 'Tomuto filtru nic neodpovídá.',
    approvedTitle: 'Schváleno: {what}',
    declinedTitle: 'Zamítnuto: {what}',
    approvalDetail: 'Navrhl/a {proposer} · rozhodl/a {decider}',
    approvalWritten: 'Zapsáno {done} z {total}',
    viewRecord: 'Zobrazit záznam',
    changeTitle: '{title} · koncept v{version}',
    changeFor: 'od tvůrce pro {who}',
    publishTitle: 'Publikováno v{version} pro {audience}',
    publishDetail: '{people} lidí',
    publishRequested: 'Od {who} na žádost uživatele {requester}',
    unpublishTitle: 'Zrušeno publikování v{version}',
    restoreTitle: 'Obnoveno: {what}',
    restoreTarget: 'Do {target}',
    refreshOk: 'Aktualizováno',
    refreshFailed: 'Aktualizace selhala',
    sourceViewed: 'Zdroj zobrazen: {path}',
    skippedTitle: '{run} přeskočil/a {part}',
    skippedBody:
      'Čte vlastní {system} každé osoby a toto spuštění nemá osobu, za kterou by mohlo číst. Vyberte, za koho se má spustit, nebo přepněte {part} na vaše připojení.',
    blockedApproval: '{title} · čeká na schválení',
    blockedBudget: '{title} · pozastaveno kvůli rozpočtu',
    blockedProvider: '{title} · čeká na poskytovatele',
    chooseRunAs: 'Vyberte, za koho se má spustit',
    changeInChat: 'Změnit v chatu',
    viaOrigin: 'prostřednictvím {label}',
    byBuilder: 'od tvůrce',
  },
  da: {
    filterAll: 'Alle',
    filterEntries: 'Poster',
    filterApprovals: 'Godkendelser',
    filterChanges: 'Ændringer',
    filterPublishes: 'Udgivelser',
    filtersLabel: 'Filtrer historikken',
    note: 'Personer, der bruger appen, kan kun se deres egne poster her.',
    today: 'I dag',
    yesterday: 'I går, {day}',
    empty: 'Der er ikke noget her endnu.',
    emptyFiltered: 'Intet matcher dette filter.',
    approvedTitle: 'Godkendt: {what}',
    declinedTitle: 'Afvist: {what}',
    approvalDetail: 'Foreslået af {proposer} · afgjort af {decider}',
    approvalWritten: '{done} af {total} skrevet',
    viewRecord: 'Vis post',
    changeTitle: '{title} · kladde v{version}',
    changeFor: 'af udvikleren, til {who}',
    publishTitle: 'Udgivet v{version} til {audience}',
    publishDetail: '{people} personer',
    publishRequested: 'Af {who} på anmodning fra {requester}',
    unpublishTitle: 'Ikke længere udgivet v{version}',
    restoreTitle: 'Gendannet {what}',
    restoreTarget: 'Til {target}',
    refreshOk: 'Opdateret',
    refreshFailed: 'Opdatering mislykkedes',
    sourceViewed: 'Kilde vist: {path}',
    skippedTitle: '{run} sprang {part} over',
    skippedBody:
      'Den læser hver persons egen {system}, og denne kørsel har ingen person at læse som. Vælg, hvem den skal køre som, eller skift {part} til din forbindelse.',
    blockedApproval: '{title} · afventer godkendelse',
    blockedBudget: '{title} · sat på pause ved budgetgrænsen',
    blockedProvider: '{title} · afventer udbyderen',
    chooseRunAs: 'Vælg, hvem den skal køre som',
    changeInChat: 'Ændr i chatten',
    viaOrigin: 'via {label}',
    byBuilder: 'af udvikleren',
  },
  de: {
    filterAll: 'Alle',
    filterEntries: 'Einträge',
    filterApprovals: 'Genehmigungen',
    filterChanges: 'Änderungen',
    filterPublishes: 'Veröffentlichungen',
    filtersLabel: 'Chronik filtern',
    note: 'Personen, die die App verwenden, sehen hier nur ihre eigenen Einträge.',
    today: 'Heute',
    yesterday: 'Gestern, {day}',
    empty: 'Noch nichts vorhanden.',
    emptyFiltered: 'Keine Einträge entsprechen diesem Filter.',
    approvedTitle: 'Genehmigt: {what}',
    declinedTitle: 'Abgelehnt: {what}',
    approvalDetail: 'Vorgeschlagen von {proposer} · entschieden von {decider}',
    approvalWritten: '{done} von {total} geschrieben',
    viewRecord: 'Datensatz anzeigen',
    changeTitle: '{title} · Entwurf v{version}',
    changeFor: 'vom Ersteller für {who}',
    publishTitle: 'v{version} für {audience} veröffentlicht',
    publishDetail: '{people} Personen',
    publishRequested: 'Von {who}, auf Anfrage von {requester}',
    unpublishTitle: 'v{version} zurückgezogen',
    restoreTitle: '{what} wiederhergestellt',
    restoreTarget: 'Nach {target}',
    refreshOk: 'Aktualisiert',
    refreshFailed: 'Aktualisierung fehlgeschlagen',
    sourceViewed: 'Quelle angezeigt: {path}',
    skippedTitle: '{run} hat {part} übersprungen',
    skippedBody:
      'Es liest das eigene {system} jeder Person, und für diesen Lauf ist keine Person festgelegt, als die es ausgeführt wird. Wählen Sie aus, als wer es ausgeführt wird, oder stellen Sie {part} auf Ihre Verbindung um.',
    blockedApproval: '{title} · wartet auf Genehmigung',
    blockedBudget: '{title} · beim Budget pausiert',
    blockedProvider: '{title} · wartet auf den Anbieter',
    chooseRunAs: 'Auswählen, als wer es ausgeführt wird',
    changeInChat: 'Änderung im Chat',
    viaOrigin: 'über {label}',
    byBuilder: 'vom Ersteller',
  },
  el: {
    filterAll: 'Όλα',
    filterEntries: 'Καταχωρίσεις',
    filterApprovals: 'Εγκρίσεις',
    filterChanges: 'Αλλαγές',
    filterPublishes: 'Δημοσιεύσεις',
    filtersLabel: 'Φιλτράρισμα ιστορικού',
    note: 'Τα άτομα που χρησιμοποιούν την Εφαρμογή βλέπουν εδώ μόνο τις δικές τους καταχωρίσεις.',
    today: 'Σήμερα',
    yesterday: 'Χθες, {day}',
    empty: 'Δεν υπάρχει τίποτα ακόμη εδώ.',
    emptyFiltered: 'Κανένα αποτέλεσμα για αυτό το φίλτρο.',
    approvedTitle: 'Εγκρίθηκε: {what}',
    declinedTitle: 'Απορρίφθηκε: {what}',
    approvalDetail: 'Προτάθηκε από {proposer} · αποφασίστηκε από {decider}',
    approvalWritten: 'Εγγράφηκαν {done} από {total}',
    viewRecord: 'Προβολή εγγραφής',
    changeTitle: '{title} · πρόχειρη έκδοση v{version}',
    changeFor: 'από τον δημιουργό, για {who}',
    publishTitle: 'Δημοσιεύτηκε η v{version} για {audience}',
    publishDetail: '{people} άτομα',
    publishRequested: 'Από τον/την {who}, κατόπιν αιτήματος του/της {requester}',
    unpublishTitle: 'Καταργήθηκε η δημοσίευση της v{version}',
    restoreTitle: 'Έγινε επαναφορά του/της {what}',
    restoreTarget: 'Στο/στην {target}',
    refreshOk: 'Ανανεώθηκε',
    refreshFailed: 'Η ανανέωση απέτυχε',
    sourceViewed: 'Προβλήθηκε η πηγή: {path}',
    skippedTitle: 'Το {run} παρέλειψε το {part}',
    skippedBody:
      'Διαβάζει το προσωπικό {system} κάθε ατόμου και αυτή η εκτέλεση δεν έχει άτομο για να χρησιμοποιήσει ως ταυτότητα. Επιλέξτε ως ποιον θα εκτελείται ή αλλάξτε το {part} στη σύνδεσή σας.',
    blockedApproval: '{title} · αναμονή έγκρισης',
    blockedBudget: '{title} · σε παύση λόγω ορίου',
    blockedProvider: '{title} · αναμονή παρόχου',
    chooseRunAs: 'Επιλέξτε ως ποιον θα εκτελείται',
    changeInChat: 'Αλλαγή στη συνομιλία',
    viaOrigin: 'μέσω {label}',
    byBuilder: 'από τον δημιουργό',
  },
  es: {
    filterAll: 'Todo',
    filterEntries: 'Entradas',
    filterApprovals: 'Aprobaciones',
    filterChanges: 'Cambios',
    filterPublishes: 'Publicaciones',
    filtersLabel: 'Filtrar el historial',
    note: 'Las personas que usan la aplicación solo ven aquí sus propias entradas.',
    today: 'Hoy',
    yesterday: 'Ayer, {day}',
    empty: 'Aún no hay nada aquí.',
    emptyFiltered: 'Nada coincide con este filtro.',
    approvedTitle: 'Aprobado: {what}',
    declinedTitle: 'Rechazado: {what}',
    approvalDetail: 'Propuesto por {proposer} · decidido por {decider}',
    approvalWritten: 'Se han escrito {done} de {total}',
    viewRecord: 'Ver registro',
    changeTitle: '{title} · borrador v{version}',
    changeFor: 'por el creador, para {who}',
    publishTitle: 'Publicado v{version} para {audience}',
    publishDetail: '{people} personas',
    publishRequested: 'Por {who}, a petición de {requester}',
    unpublishTitle: 'Se canceló la publicación de v{version}',
    restoreTitle: 'Restaurado: {what}',
    restoreTarget: 'A {target}',
    refreshOk: 'Actualizado',
    refreshFailed: 'No se pudo actualizar',
    sourceViewed: 'Fuente consultada: {path}',
    skippedTitle: '{run} omitió {part}',
    skippedBody:
      'Lee el {system} propio de cada persona y esta ejecución no tiene una persona con cuya identidad leer. Elige con qué identidad se ejecuta o cambia {part} a tu conexión.',
    blockedApproval: '{title} · esperando aprobación',
    blockedBudget: '{title} · pausado por el presupuesto',
    blockedProvider: '{title} · esperando al proveedor',
    chooseRunAs: 'Elegir con qué identidad se ejecuta',
    changeInChat: 'Cambiar en el chat',
    viaOrigin: 'mediante {label}',
    byBuilder: 'por el creador',
  },
  et: {
    filterAll: 'Kõik',
    filterEntries: 'Kirjed',
    filterApprovals: 'Kinnitused',
    filterChanges: 'Muudatused',
    filterPublishes: 'Avaldamised',
    filtersLabel: 'Filtreeri ajalugu',
    note: 'Rakenduse kasutajad näevad siin ainult enda kirjeid.',
    today: 'Täna',
    yesterday: 'Eile, {day}',
    empty: 'Siin pole veel midagi.',
    emptyFiltered: 'Sellele filtrile ei vasta midagi.',
    approvedTitle: 'Kinnitatud: {what}',
    declinedTitle: 'Tagasi lükatud: {what}',
    approvalDetail: 'Esitas {proposer} · otsustas {decider}',
    approvalWritten: 'Kirjutatud {done} / {total}',
    viewRecord: 'Vaata kirjet',
    changeTitle: '{title} · mustand v{version}',
    changeFor: 'koostajalt, kasutajale {who}',
    publishTitle: 'Avaldati v{version} sihtrühmale {audience}',
    publishDetail: '{people} inimest',
    publishRequested: '{who} · {requester} taotlusel',
    unpublishTitle: 'Võeti v{version} avaldusest maha',
    restoreTitle: 'Taastati {what}',
    restoreTarget: 'Sihtkohta {target}',
    refreshOk: 'Värskendatud',
    refreshFailed: 'Värskendamine ebaõnnestus',
    sourceViewed: 'Allikat vaadatud: {path}',
    skippedTitle: '{run} jättis osa {part} vahele',
    skippedBody:
      'See loeb iga inimese enda {system} kaudu, kuid sellel käivitamisel pole isikut, kellena lugeda. Vali, kellena see käivitatakse, või lülita {part} sinu ühendusele.',
    blockedApproval: '{title} · ootab kinnitust',
    blockedBudget: '{title} · eelarve juures peatatud',
    blockedProvider: '{title} · ootab teenusepakkujat',
    chooseRunAs: 'Vali, kellena see käivitatakse',
    changeInChat: 'Muuda vestluses',
    viaOrigin: '{label} kaudu',
    byBuilder: 'koostajalt',
  },
  fi: {
    filterAll: 'Kaikki',
    filterEntries: 'Merkinnät',
    filterApprovals: 'Hyväksynnät',
    filterChanges: 'Muutokset',
    filterPublishes: 'Julkaisut',
    filtersLabel: 'Suodata historiaa',
    note: 'Sovellusta käyttävät henkilöt näkevät täällä vain omat merkintänsä.',
    today: 'Tänään',
    yesterday: 'Eilen, {day}',
    empty: 'Täällä ei ole vielä mitään.',
    emptyFiltered: 'Mikään ei vastaa tätä suodatinta.',
    approvedTitle: 'Hyväksytty: {what}',
    declinedTitle: 'Hylätty: {what}',
    approvalDetail: 'Ehdottaja: {proposer} · päättäjä: {decider}',
    approvalWritten: '{done}/{total} kirjoitettu',
    viewRecord: 'Näytä tietue',
    changeTitle: '{title} · luonnos v{version}',
    changeFor: 'rakentajalta henkilölle {who}',
    publishTitle: 'Julkaistu versiona v{version} kohteelle {audience}',
    publishDetail: '{people} henkilöä',
    publishRequested: '{who}, pyytäjän {requester} pyynnöstä',
    unpublishTitle: 'Julkaisu peruttu versiolta v{version}',
    restoreTitle: 'Palautettu: {what}',
    restoreTarget: 'Kohteeseen {target}',
    refreshOk: 'Päivitetty',
    refreshFailed: 'Päivitys epäonnistui',
    sourceViewed: 'Lähde katsottu: {path}',
    skippedTitle: '{run} ohitti osan {part}',
    skippedBody:
      'Se lukee kunkin henkilön oman järjestelmän {system} tietoja, eikä tällä suorituksella ole henkilöä, jonkaä se voisi käyttää. Valitse, kenenä se suoritetaan, tai vaihda {part} käyttämään yhteyttäsi.',
    blockedApproval: '{title} · odottaa hyväksyntää',
    blockedBudget: '{title} · keskeytetty budjetin kohdalla',
    blockedProvider: '{title} · odottaa palveluntarjoajaa',
    chooseRunAs: 'Valitse, kenenä se suoritetaan',
    changeInChat: 'Muutos keskustelussa',
    viaOrigin: 'lähteen {label} kautta',
    byBuilder: 'rakentajalta',
  },
  fr: {
    filterAll: 'Tout',
    filterEntries: 'Entrées',
    filterApprovals: 'Approbations',
    filterChanges: 'Modifications',
    filterPublishes: 'Publications',
    filtersLabel: 'Filtrer l’historique',
    note: 'Les personnes qui utilisent l’application ne voient ici que leurs propres entrées.',
    today: 'Aujourd’hui',
    yesterday: 'Hier, {day}',
    empty: 'Rien ici pour le moment.',
    emptyFiltered: 'Aucun élément ne correspond à ce filtre.',
    approvedTitle: 'Approuvé : {what}',
    declinedTitle: 'Refusé : {what}',
    approvalDetail: 'Proposé par {proposer} · décidé par {decider}',
    approvalWritten: '{done} sur {total} enregistrés',
    viewRecord: 'Afficher l’enregistrement',
    changeTitle: '{title} · brouillon v{version}',
    changeFor: 'par le concepteur, pour {who}',
    publishTitle: 'Publié v{version} pour {audience}',
    publishDetail: '{people} personnes',
    publishRequested: 'Par {who}, à la demande de {requester}',
    unpublishTitle: 'Dépublié v{version}',
    restoreTitle: '{what} restauré',
    restoreTarget: 'Vers {target}',
    refreshOk: 'Actualisé',
    refreshFailed: 'Échec de l’actualisation',
    sourceViewed: 'Source consultée : {path}',
    skippedTitle: '{run} a ignoré {part}',
    skippedBody:
      'Il lit le {system} de chaque personne, et cette exécution n’a personne au nom de qui lire. Choisissez au nom de qui l’exécuter ou basculez {part} vers votre connexion.',
    blockedApproval: '{title} · en attente d’approbation',
    blockedBudget: '{title} · en pause à la limite budgétaire',
    blockedProvider: '{title} · en attente du fournisseur',
    chooseRunAs: 'Choisir au nom de qui l’exécuter',
    changeInChat: 'Modifier dans le chat',
    viaOrigin: 'via {label}',
    byBuilder: 'par le concepteur',
  },
  ga: {
    filterAll: 'Gach rud',
    filterEntries: 'Iontrálacha',
    filterApprovals: 'Ceaduithe',
    filterChanges: 'Athruithe',
    filterPublishes: 'Foilseacháin',
    filtersLabel: 'Scag an stair',
    note: 'Ní fheiceann daoine a úsáideann an aip ach a n-iontrálacha féin anseo.',
    today: 'Inniu',
    yesterday: 'Inné, {day}',
    empty: 'Níl aon rud anseo fós.',
    emptyFiltered: 'Níl aon rud ag teacht leis an scagaire seo.',
    approvedTitle: 'Ceadaithe: {what}',
    declinedTitle: 'Diúltaithe: {what}',
    approvalDetail: 'Molta ag {proposer} · socraithe ag {decider}',
    approvalWritten: 'Scríofa: {done} as {total}',
    viewRecord: 'Féach ar an taifead',
    changeTitle: '{title} · dréacht v{version}',
    changeFor: 'ag an tógálaí, do {who}',
    publishTitle: 'Foilsithe v{version} chuig {audience}',
    publishDetail: '{people} duine',
    publishRequested: 'Ag {who}, ar iarratas {requester}',
    unpublishTitle: 'Neamhfhoilsithe v{version}',
    restoreTitle: 'Athchóirithe: {what}',
    restoreTarget: 'Chuige seo: {target}',
    refreshOk: 'Athnuaite',
    refreshFailed: 'Theip ar an athnuachan',
    sourceViewed: 'Foinse feicthe: {path}',
    skippedTitle: 'Scipeáil {run} {part}',
    skippedBody:
      'Léann sé {system} gach duine féin, agus níl aon duine ag an rith seo le léamh mar é. Roghnaigh cé hé a ritheann sé mar, nó aistrigh {part} chuig do nasc.',
    blockedApproval: '{title} · ag fanacht le ceadú',
    blockedBudget: '{title} · curtha ar sos ag an mbuiséad',
    blockedProvider: '{title} · ag fanacht leis an soláthraí',
    chooseRunAs: 'Roghnaigh cé hé a ritheann sé mar',
    changeInChat: 'Athrú sa chomhrá',
    viaOrigin: 'trí {label}',
    byBuilder: 'ag an tógálaí',
  },
  hr: {
    filterAll: 'Sve',
    filterEntries: 'Unosi',
    filterApprovals: 'Odobrenja',
    filterChanges: 'Promjene',
    filterPublishes: 'Objave',
    filtersLabel: 'Filtriraj povijest',
    note: 'Osobe koje upotrebljavaju aplikaciju ovdje vide samo vlastite unose.',
    today: 'Danas',
    yesterday: 'Jučer, {day}',
    empty: 'Ovdje još nema ničega.',
    emptyFiltered: 'Nijedan rezultat ne odgovara ovom filtru.',
    approvedTitle: 'Odobreno: {what}',
    declinedTitle: 'Odbijeno: {what}',
    approvalDetail: 'Predložio/la {proposer} · odlučio/la {decider}',
    approvalWritten: 'Zapisano: {done} od ukupno {total}',
    viewRecord: 'Prikaži zapis',
    changeTitle: '{title} · nacrt v{version}',
    changeFor: 'izradio/la graditelj, za {who}',
    publishTitle: 'Objavljeno v{version} za {audience}',
    publishDetail: '{people} osoba',
    publishRequested: '{who}, na zahtjev osobe {requester}',
    unpublishTitle: 'Poništena objava v{version}',
    restoreTitle: 'Obnovljeno: {what}',
    restoreTarget: 'U {target}',
    refreshOk: 'Osvježeno',
    refreshFailed: 'Osvježavanje nije uspjelo',
    sourceViewed: 'Izvor pregledan: {path}',
    skippedTitle: '{run} je preskočio/la {part}',
    skippedBody:
      'Čita vlastiti {system} svake osobe, a ovaj se postupak nema kao tko izvršiti. Odaberite kao tko će se pokrenuti ili prebacite {part} na svoju vezu.',
    blockedApproval: '{title} · čeka odobrenje',
    blockedBudget: '{title} · zaustavljeno zbog proračuna',
    blockedProvider: '{title} · čeka pružatelja usluge',
    chooseRunAs: 'Odaberite kao tko će se pokrenuti',
    changeInChat: 'Promjena u chatu',
    viaOrigin: 'putem {label}',
    byBuilder: 'izradio/la graditelj',
  },
  hu: {
    filterAll: 'Összes',
    filterEntries: 'Bejegyzések',
    filterApprovals: 'Jóváhagyások',
    filterChanges: 'Módosítások',
    filterPublishes: 'Közzétételek',
    filtersLabel: 'Előzmények szűrése',
    note: 'Az alkalmazást használók itt csak a saját bejegyzéseiket látják.',
    today: 'Ma',
    yesterday: 'Tegnap, {day}',
    empty: 'Itt még nincs semmi.',
    emptyFiltered: 'Nincs a szűrőnek megfelelő elem.',
    approvedTitle: 'Jóváhagyva: {what}',
    declinedTitle: 'Elutasítva: {what}',
    approvalDetail: 'Javasolta: {proposer} · döntött: {decider}',
    approvalWritten: '{done} / {total} kiírva',
    viewRecord: 'Rekord megtekintése',
    changeTitle: '{title} · v{version} piszkozat',
    changeFor: 'a készítő által, {who} számára',
    publishTitle: 'v{version} közzétéve ennek: {audience}',
    publishDetail: '{people} személy',
    publishRequested: '{who} által, {requester} kérésére',
    unpublishTitle: 'v{version} visszavonva',
    restoreTitle: 'Visszaállítva: {what}',
    restoreTarget: 'Ide: {target}',
    refreshOk: 'Frissítve',
    refreshFailed: 'A frissítés sikertelen',
    sourceViewed: 'Forrás megtekintve: {path}',
    skippedTitle: '{run} kihagyta ezt: {part}',
    skippedBody:
      'Mindenki a saját {system} rendszerét olvassa, ez a futtatás pedig nem tudja, kinek a nevében olvasson. Válaszd ki, kinek a nevében fusson, vagy állítsd át ezt: {part} a saját kapcsolatodra.',
    blockedApproval: '{title} · jóváhagyásra vár',
    blockedBudget: '{title} · a költségkeretnél szüneteltetve',
    blockedProvider: '{title} · a szolgáltatóra vár',
    chooseRunAs: 'Válaszd ki, kinek a nevében fusson',
    changeInChat: 'Módosítás a csevegésben',
    viaOrigin: 'ezen keresztül: {label}',
    byBuilder: 'a készítő által',
  },
  it: {
    filterAll: 'Tutto',
    filterEntries: 'Voci',
    filterApprovals: 'Approvazioni',
    filterChanges: 'Modifiche',
    filterPublishes: 'Pubblicazioni',
    filtersLabel: 'Filtra la cronologia',
    note: 'Chi usa l’app vede qui solo le proprie voci.',
    today: 'Oggi',
    yesterday: 'Ieri, {day}',
    empty: 'Non c’è ancora nulla qui.',
    emptyFiltered: 'Nessun elemento corrisponde a questo filtro.',
    approvedTitle: 'Approvato: {what}',
    declinedTitle: 'Rifiutato: {what}',
    approvalDetail: 'Proposto da {proposer} · deciso da {decider}',
    approvalWritten: '{done} di {total} scritti',
    viewRecord: 'Visualizza il record',
    changeTitle: '{title} · bozza v{version}',
    changeFor: 'dall’autore, per {who}',
    publishTitle: 'Pubblicato v{version} per {audience}',
    publishDetail: '{people} persone',
    publishRequested: 'Da {who}, su richiesta di {requester}',
    unpublishTitle: 'Pubblicazione annullata per v{version}',
    restoreTitle: 'Ripristinato {what}',
    restoreTarget: 'In {target}',
    refreshOk: 'Aggiornato',
    refreshFailed: 'Aggiornamento non riuscito',
    sourceViewed: 'Origine visualizzata: {path}',
    skippedTitle: '{run} ha saltato {part}',
    skippedBody:
      'Legge il {system} personale di ogni persona e questa esecuzione non ha una persona con cui identificarsi. Scegli con quale persona eseguirla oppure imposta {part} sulla tua connessione.',
    blockedApproval: '{title} · in attesa di approvazione',
    blockedBudget: '{title} · in pausa per il limite di budget',
    blockedProvider: '{title} · in attesa del provider',
    chooseRunAs: 'Scegli con quale persona eseguirla',
    changeInChat: 'Modifica nella chat',
    viaOrigin: 'tramite {label}',
    byBuilder: 'dall’autore',
  },
  lt: {
    filterAll: 'Visi',
    filterEntries: 'Įrašai',
    filterApprovals: 'Patvirtinimai',
    filterChanges: 'Pakeitimai',
    filterPublishes: 'Publikavimai',
    filtersLabel: 'Filtruoti istoriją',
    note: 'Programą naudojantys žmonės čia mato tik savo įrašus.',
    today: 'Šiandien',
    yesterday: 'Vakar, {day}',
    empty: 'Kol kas čia nieko nėra.',
    emptyFiltered: 'Šio filtro neatitinka niekas.',
    approvedTitle: 'Patvirtinta: {what}',
    declinedTitle: 'Atmesta: {what}',
    approvalDetail: 'Pasiūlė {proposer} · sprendimą priėmė {decider}',
    approvalWritten: 'Įrašyta {done} iš {total}',
    viewRecord: 'Peržiūrėti įrašą',
    changeTitle: '{title} · juodraščio v{version}',
    changeFor: 'kūrėjo, skirta {who}',
    publishTitle: 'Paskelbta v{version}, skirta {audience}',
    publishDetail: '{people} žmonių',
    publishRequested: '{who}, {requester} prašymu',
    unpublishTitle: 'Nepaskelbta v{version}',
    restoreTitle: 'Atkurta: {what}',
    restoreTarget: 'Į {target}',
    refreshOk: 'Atnaujinta',
    refreshFailed: 'Atnaujinti nepavyko',
    sourceViewed: 'Peržiūrėtas šaltinis: {path}',
    skippedTitle: '{run} praleido {part}',
    skippedBody:
      'Jis nuskaito kiekvieno asmens {system}, o šis paleidimas neturi asmens, kuriuo galėtų būti laikomas. Pasirinkite, kuo jis paleidžiamas, arba perjunkite {part} į savo ryšį.',
    blockedApproval: '{title} · laukiama patvirtinimo',
    blockedBudget: '{title} · pristabdyta pasiekus biudžetą',
    blockedProvider: '{title} · laukiama teikėjo',
    chooseRunAs: 'Pasirinkite, kuo paleidžiama',
    changeInChat: 'Pakeisti pokalbyje',
    viaOrigin: 'per {label}',
    byBuilder: 'kūrėjo',
  },
  lv: {
    filterAll: 'Visi',
    filterEntries: 'Ieraksti',
    filterApprovals: 'Apstiprinājumi',
    filterChanges: 'Izmaiņas',
    filterPublishes: 'Publicēšanas',
    filtersLabel: 'Filtrēt vēsturi',
    note: 'Lietotnes lietotāji šeit redz tikai savus ierakstus.',
    today: 'Šodien',
    yesterday: 'Vakar, {day}',
    empty: 'Pagaidām nekā nav.',
    emptyFiltered: 'Šim filtram nekas neatbilst.',
    approvedTitle: 'Apstiprināts: {what}',
    declinedTitle: 'Noraidīts: {what}',
    approvalDetail: 'Ierosināja {proposer} · lēmumu pieņēma {decider}',
    approvalWritten: 'Ierakstīts: {done} no {total}',
    viewRecord: 'Skatīt ierakstu',
    changeTitle: '{title} · melnraksts v{version}',
    changeFor: 'veidotājs, paredzēts: {who}',
    publishTitle: 'Publicēts v{version} auditorijai {audience}',
    publishDetail: '{people} personas',
    publishRequested: '{who} pēc {requester} pieprasījuma',
    unpublishTitle: 'Atsaukta publicēšana v{version}',
    restoreTitle: 'Atjaunots: {what}',
    restoreTarget: 'Uz: {target}',
    refreshOk: 'Atjaunināts',
    refreshFailed: 'Neizdevās atjaunināt',
    sourceViewed: 'Skatīts avots: {path}',
    skippedTitle: '{run} izlaida {part}',
    skippedBody:
      'Tas nolasa katras personas {system} un šai palaišanai nav personas, kuras vārdā lasīt. Izvēlieties, kuras personas vārdā tas tiek palaists, vai pārslēdziet {part} uz savu savienojumu.',
    blockedApproval: '{title} · gaida apstiprinājumu',
    blockedBudget: '{title} · apturēts pie budžeta limita',
    blockedProvider: '{title} · gaida pakalpojumu sniedzēju',
    chooseRunAs: 'Izvēlieties, kuras personas vārdā palaist',
    changeInChat: 'Mainīt tērzēšanā',
    viaOrigin: 'izmantojot {label}',
    byBuilder: 'veidotājs',
  },
  mt: {
    filterAll: 'Kollha',
    filterEntries: 'Entrati',
    filterApprovals: 'Approvazzjonijiet',
    filterChanges: 'Bidliet',
    filterPublishes: 'Pubblikazzjonijiet',
    filtersLabel: 'Iffiltra l-istorja',
    note: 'In-nies li jużaw l-App jaraw hawn biss l-entrati tagħhom.',
    today: 'Illum',
    yesterday: 'Ilbieraħ, {day}',
    empty: 'Għad m’hemm xejn hawn.',
    emptyFiltered: 'Xejn ma jaqbel ma’ dan il-filtru.',
    approvedTitle: 'Approvat: {what}',
    declinedTitle: 'Miċħud: {what}',
    approvalDetail: 'Propost minn {proposer} · deċiż minn {decider}',
    approvalWritten: '{done} minn {total} miktubin',
    viewRecord: 'Ara r-rekord',
    changeTitle: '{title} · abbozz v{version}',
    changeFor: 'mill-bennej, għal {who}',
    publishTitle: 'Ippubblikat v{version} lil {audience}',
    publishDetail: '{people} persuni',
    publishRequested: 'Minn {who}, fuq talba ta’ {requester}',
    unpublishTitle: 'Tneħħiet il-pubblikazzjoni ta’ v{version}',
    restoreTitle: 'Ġie rrestawrat {what}',
    restoreTarget: 'Għal {target}',
    refreshOk: 'Aġġornat',
    refreshFailed: 'L-aġġornament falla',
    sourceViewed: 'Is-sors deher: {path}',
    skippedTitle: '{run} qabeż {part}',
    skippedBody:
      'Dan jaqra {system} personali ta’ kull persuna, u din l-eżekuzzjoni m’għandhiex persuna li taqra bħala. Agħżel bħala min titħaddem, jew aqleb {part} għall-konnessjoni tiegħek.',
    blockedApproval: '{title} · qed tistenna l-approvazzjoni',
    blockedBudget: '{title} · waqaf mal-baġit',
    blockedProvider: '{title} · qed jistenna lill-fornitur',
    chooseRunAs: 'Agħżel bħala min titħaddem',
    changeInChat: 'Bidla fiċ-chat',
    viaOrigin: 'permezz ta’ {label}',
    byBuilder: 'mill-bennej',
  },
  nl: {
    filterAll: 'Alles',
    filterEntries: 'Vermeldingen',
    filterApprovals: 'Goedkeuringen',
    filterChanges: 'Wijzigingen',
    filterPublishes: 'Publicaties',
    filtersLabel: 'Geschiedenis filteren',
    note: 'Mensen die de app gebruiken, zien hier alleen hun eigen vermeldingen.',
    today: 'Vandaag',
    yesterday: 'Gisteren, {day}',
    empty: 'Hier is nog niets.',
    emptyFiltered: 'Niets komt overeen met dit filter.',
    approvedTitle: 'Goedgekeurd: {what}',
    declinedTitle: 'Afgewezen: {what}',
    approvalDetail: 'Voorgesteld door {proposer} · besloten door {decider}',
    approvalWritten: '{done} van {total} geschreven',
    viewRecord: 'Record bekijken',
    changeTitle: '{title} · concept v{version}',
    changeFor: 'door de bouwer, voor {who}',
    publishTitle: 'v{version} gepubliceerd voor {audience}',
    publishDetail: '{people} mensen',
    publishRequested: 'Door {who}, op verzoek van {requester}',
    unpublishTitle: 'Publicatie van v{version} ingetrokken',
    restoreTitle: '{what} hersteld',
    restoreTarget: 'Naar {target}',
    refreshOk: 'Vernieuwd',
    refreshFailed: 'Vernieuwen mislukt',
    sourceViewed: 'Bron bekeken: {path}',
    skippedTitle: '{run} heeft {part} overgeslagen',
    skippedBody:
      'Het leest ieders eigen {system}, maar deze uitvoering heeft niemand om als te lezen. Kies namens wie het wordt uitgevoerd, of schakel {part} over naar jouw verbinding.',
    blockedApproval: '{title} · wachten op goedkeuring',
    blockedBudget: '{title} · gepauzeerd bij het budget',
    blockedProvider: '{title} · wachten op de provider',
    chooseRunAs: 'Kies namens wie het wordt uitgevoerd',
    changeInChat: 'Wijzigen in chat',
    viaOrigin: 'via {label}',
    byBuilder: 'door de bouwer',
  },
  pl: {
    filterAll: 'Wszystko',
    filterEntries: 'Wpisy',
    filterApprovals: 'Zgody',
    filterChanges: 'Zmiany',
    filterPublishes: 'Publikacje',
    filtersLabel: 'Filtruj historię',
    note: 'Osoby korzystające z aplikacji widzą tutaj tylko własne wpisy.',
    today: 'Dzisiaj',
    yesterday: 'Wczoraj, {day}',
    empty: 'Nie ma tu jeszcze żadnych wpisów.',
    emptyFiltered: 'Nic nie pasuje do tego filtra.',
    approvedTitle: 'Zatwierdzono: {what}',
    declinedTitle: 'Odrzucono: {what}',
    approvalDetail: 'Zaproponował(a): {proposer} · decyzję podjął(-ęła): {decider}',
    approvalWritten: 'Zapisano {done} z {total}',
    viewRecord: 'Wyświetl rekord',
    changeTitle: '{title} · wersja robocza v{version}',
    changeFor: 'przez twórcę, dla {who}',
    publishTitle: 'Opublikowano wersję v{version} dla {audience}',
    publishDetail: '{people} osób',
    publishRequested: 'Przez {who}, na prośbę {requester}',
    unpublishTitle: 'Wycofano publikację wersji v{version}',
    restoreTitle: 'Przywrócono {what}',
    restoreTarget: 'Do {target}',
    refreshOk: 'Odświeżono',
    refreshFailed: 'Odświeżanie nie powiodło się',
    sourceViewed: 'Wyświetlono źródło: {path}',
    skippedTitle: '{run} pominięto {part}',
    skippedBody:
      'Odczytuje własny {system} każdej osoby, a dla tego uruchomienia nie wybrano osoby, w imieniu której ma działać. Wybierz, w czyim imieniu ma działać, albo przełącz {part} na swoje połączenie.',
    blockedApproval: '{title} · oczekuje na zatwierdzenie',
    blockedBudget: '{title} · wstrzymano po osiągnięciu limitu',
    blockedProvider: '{title} · oczekuje na dostawcę',
    chooseRunAs: 'Wybierz, w czyim imieniu ma działać',
    changeInChat: 'Zmień na czacie',
    viaOrigin: 'przez {label}',
    byBuilder: 'przez twórcę',
  },
  pt: {
    filterAll: 'Tudo',
    filterEntries: 'Entradas',
    filterApprovals: 'Aprovações',
    filterChanges: 'Alterações',
    filterPublishes: 'Publicações',
    filtersLabel: 'Filtrar o histórico',
    note: 'As pessoas que usam o aplicativo veem aqui apenas as próprias entradas.',
    today: 'Hoje',
    yesterday: 'Ontem, {day}',
    empty: 'Nada aqui ainda.',
    emptyFiltered: 'Nada corresponde a este filtro.',
    approvedTitle: 'Aprovado: {what}',
    declinedTitle: 'Recusado: {what}',
    approvalDetail: 'Proposto por {proposer} · decidido por {decider}',
    approvalWritten: '{done} de {total} gravados',
    viewRecord: 'Ver registro',
    changeTitle: '{title} · rascunho v{version}',
    changeFor: 'pelo criador, para {who}',
    publishTitle: 'Publicado v{version} para {audience}',
    publishDetail: '{people} pessoas',
    publishRequested: 'Por {who}, a pedido de {requester}',
    unpublishTitle: 'Despublicado v{version}',
    restoreTitle: 'Restaurado {what}',
    restoreTarget: 'Para {target}',
    refreshOk: 'Atualizado',
    refreshFailed: 'Falha ao atualizar',
    sourceViewed: 'Fonte visualizada: {path}',
    skippedTitle: '{run} ignorou {part}',
    skippedBody:
      'Ele lê o {system} de cada pessoa, e esta execução não tem uma pessoa em cujo nome ler. Escolha em nome de quem ele será executado ou mude {part} para sua conexão.',
    blockedApproval: '{title} · aguardando aprovação',
    blockedBudget: '{title} · pausado no limite',
    blockedProvider: '{title} · aguardando o provedor',
    chooseRunAs: 'Escolher em nome de quem executar',
    changeInChat: 'Alterar no chat',
    viaOrigin: 'via {label}',
    byBuilder: 'pelo criador',
  },
  ro: {
    filterAll: 'Toate',
    filterEntries: 'Înregistrări',
    filterApprovals: 'Aprobări',
    filterChanges: 'Modificări',
    filterPublishes: 'Publicări',
    filtersLabel: 'Filtrează istoricul',
    note: 'Persoanele care folosesc aplicația văd aici doar propriile înregistrări.',
    today: 'Astăzi',
    yesterday: 'Ieri, {day}',
    empty: 'Nimic aici încă.',
    emptyFiltered: 'Nimic nu corespunde acestui filtru.',
    approvedTitle: 'Aprobat: {what}',
    declinedTitle: 'Respins: {what}',
    approvalDetail: 'Propus de {proposer} · decis de {decider}',
    approvalWritten: '{done} din {total} scrise',
    viewRecord: 'Vezi înregistrarea',
    changeTitle: '{title} · schița v{version}',
    changeFor: 'de creator, pentru {who}',
    publishTitle: 'Publicat v{version} pentru {audience}',
    publishDetail: '{people} persoane',
    publishRequested: 'De {who}, la cererea lui {requester}',
    unpublishTitle: 'Anulat de la publicare v{version}',
    restoreTitle: 'Restaurat: {what}',
    restoreTarget: 'La {target}',
    refreshOk: 'Reîmprospătat',
    refreshFailed: 'Reîmprospătarea a eșuat',
    sourceViewed: 'Sursă vizualizată: {path}',
    skippedTitle: '{run} a omis {part}',
    skippedBody:
      'Citește {system}-ul propriu al fiecărei persoane, iar această rulare nu are nicio persoană în numele căreia să citească. Alege în numele cui rulează sau schimbă {part} pentru a folosi conexiunea ta.',
    blockedApproval: '{title} · în așteptarea aprobării',
    blockedBudget: '{title} · întrerupt la limita bugetului',
    blockedProvider: '{title} · în așteptarea furnizorului',
    chooseRunAs: 'Alege în numele cui rulează',
    changeInChat: 'Modifică în chat',
    viaOrigin: 'prin {label}',
    byBuilder: 'de creator',
  },
  sk: {
    filterAll: 'Všetko',
    filterEntries: 'Záznamy',
    filterApprovals: 'Schválenia',
    filterChanges: 'Zmeny',
    filterPublishes: 'Publikovania',
    filtersLabel: 'Filtrovať históriu',
    note: 'Ľudia, ktorí používajú aplikáciu, tu uvidia iba svoje vlastné záznamy.',
    today: 'Dnes',
    yesterday: 'Včera, {day}',
    empty: 'Zatiaľ tu nič nie je.',
    emptyFiltered: 'Tomuto filtru nič nezodpovedá.',
    approvedTitle: 'Schválené: {what}',
    declinedTitle: 'Zamietnuté: {what}',
    approvalDetail: 'Navrhol/a {proposer} · rozhodol/a {decider}',
    approvalWritten: 'Zapísané: {done} z {total}',
    viewRecord: 'Zobraziť záznam',
    changeTitle: '{title} · koncept v{version}',
    changeFor: 'od tvorcu pre {who}',
    publishTitle: 'Publikované v{version} pre {audience}',
    publishDetail: '{people} ľudí',
    publishRequested: 'Od {who} na žiadosť používateľa {requester}',
    unpublishTitle: 'Zrušené publikovanie v{version}',
    restoreTitle: 'Obnovené: {what}',
    restoreTarget: 'Do: {target}',
    refreshOk: 'Obnovené',
    refreshFailed: 'Obnovenie zlyhalo',
    sourceViewed: 'Zobrazený zdroj: {path}',
    skippedTitle: '{run} preskočil/a {part}',
    skippedBody:
      'Číta vlastný {system} každej osoby, no toto spustenie nemá osobu, za ktorú by mohlo čítať. Vyberte, za koho sa má spustiť, alebo prepnite {part} na svoje pripojenie.',
    blockedApproval: '{title} · čaká na schválenie',
    blockedBudget: '{title} · pozastavené pri limite rozpočtu',
    blockedProvider: '{title} · čaká na poskytovateľa',
    chooseRunAs: 'Vyberte, za koho sa má spustiť',
    changeInChat: 'Zmeniť v chate',
    viaOrigin: 'prostredníctvom {label}',
    byBuilder: 'od tvorcu',
  },
  sl: {
    filterAll: 'Vse',
    filterEntries: 'Vnosi',
    filterApprovals: 'Odobritve',
    filterChanges: 'Spremembe',
    filterPublishes: 'Objave',
    filtersLabel: 'Filtriraj zgodovino',
    note: 'Ljudje, ki uporabljajo aplikacijo, tukaj vidijo le svoje vnose.',
    today: 'Danes',
    yesterday: 'Včeraj, {day}',
    empty: 'Tukaj še ni ničesar.',
    emptyFiltered: 'Temu filtru nič ne ustreza.',
    approvedTitle: 'Odobreno: {what}',
    declinedTitle: 'Zavrnjeno: {what}',
    approvalDetail: 'Predlagal/-a {proposer} · odločil/-a {decider}',
    approvalWritten: 'Zapisano: {done} od {total}',
    viewRecord: 'Prikaži zapis',
    changeTitle: '{title} · osnutek v{version}',
    changeFor: 'po ustvarjalcu, za {who}',
    publishTitle: 'Objavljeno v{version} za {audience}',
    publishDetail: '{people} oseb',
    publishRequested: '{who}, na zahtevo osebe {requester}',
    unpublishTitle: 'Umik objave v{version}',
    restoreTitle: 'Obnovljeno: {what}',
    restoreTarget: 'V {target}',
    refreshOk: 'Osveženo',
    refreshFailed: 'Osveževanje ni uspelo',
    sourceViewed: 'Vir prikazan: {path}',
    skippedTitle: '{run} je preskočil/-a {part}',
    skippedBody:
      'Prebere lastni {system} vsake osebe, ta zagon pa nima osebe, v imenu katere bi bral. Izberite, v imenu koga naj se zažene, ali preklopite {part} na svojo povezavo.',
    blockedApproval: '{title} · čaka na odobritev',
    blockedBudget: '{title} · zaustavljeno zaradi proračuna',
    blockedProvider: '{title} · čaka na ponudnika',
    chooseRunAs: 'Izberite, v imenu koga naj se zažene',
    changeInChat: 'Spremeni v klepetu',
    viaOrigin: 'prek {label}',
    byBuilder: 'po ustvarjalcu',
  },
  sv: {
    filterAll: 'Alla',
    filterEntries: 'Poster',
    filterApprovals: 'Godkännanden',
    filterChanges: 'Ändringar',
    filterPublishes: 'Publiceringar',
    filtersLabel: 'Filtrera historiken',
    note: 'Den som använder appen ser bara sina egna poster här.',
    today: 'Idag',
    yesterday: 'Igår, {day}',
    empty: 'Inget här än.',
    emptyFiltered: 'Inget matchar filtret.',
    approvedTitle: 'Godkänt: {what}',
    declinedTitle: 'Avslaget: {what}',
    approvalDetail: 'Föreslaget av {proposer} · beslutat av {decider}',
    approvalWritten: '{done} av {total} skrivna',
    viewRecord: 'Visa underlag',
    changeTitle: '{title} · utkast v{version}',
    changeFor: 'av byggaren, åt {who}',
    publishTitle: 'Publicerade v{version} för {audience}',
    publishDetail: '{people} personer',
    publishRequested: 'Av {who}, på {requester}s begäran',
    unpublishTitle: 'Avpublicerade v{version}',
    restoreTitle: 'Återställde {what}',
    restoreTarget: 'Till {target}',
    refreshOk: 'Uppdaterad',
    refreshFailed: 'Uppdateringen misslyckades',
    sourceViewed: 'Källa visad: {path}',
    skippedTitle: '{run} hoppade över {part}',
    skippedBody:
      'Den läser var och ens eget {system}, och den här körningen har ingen person att läsa som. Välj vem den körs som, eller byt {part} till din anslutning.',
    blockedApproval: '{title} · väntar på godkännande',
    blockedBudget: '{title} · pausad vid budgeten',
    blockedProvider: '{title} · väntar på leverantören',
    chooseRunAs: 'Välj vem den körs som',
    changeInChat: 'Ändra i chatten',
    viaOrigin: 'via {label}',
    byBuilder: 'av byggaren',
  },
};
export default appHistoryCopy;
